// @givmo/compliance — pieces every compliance surface composes (CCM-WEB1).
//
// ══ THE RULE FORM IS THE SAFE FRAME, AND IT IS THE ONLY FRAME ══════════════════════════════════
//
// Everything this module says about a jurisdiction is said in ONE shape, and {@link RuleForm} is it:
//
//   • the jurisdiction's rule, transcribed;
//   • its citation, and — as a separate element — a link to the official source where one exists;
//   • its source's date, worded by what happened that day (checked live, or recorded from Givmo's
//     research notes) — the honest bound on all of it;
//   • the organization's OWN recorded facts, juxtaposed rather than combined with the rule.
//
// Juxtaposed, never combined. A rules library holds "Alabama's exemption reaches an organization at
// or below $25,000 in gross contributions"; a profile holds a recorded revenue figure. Putting them
// together to emit "you qualify" is the individualized legal conclusion this module exists to
// refuse — and the backend asserts the refusal structurally, by giving a derived candidate NO field
// to put a conclusion in. The behavioural proof is that an organization recording $1 and one
// recording $50M get IDENTICAL candidate sets. This component is the front-end half of that: it
// renders the two facts side by side and draws no line between them.
//
// ⚠ The copy says "source checked on" and "recorded". The two vocabulary roots the suite registry
// bans — the one that claims a fact was CHECKED and the one that claims it was sworn to — are spelled
// out only in `compliance-copy-law.test.ts`, which is the one file here allowed to write them (the
// scan reads this source). The backend deviated its own enum spelling for the same reason.

import { useId, type ReactNode } from 'react';
import { Badge, Card, CardBody, EmptyState, Icon, testIdProp } from '@givmo/design-system';
import { FileText, Landmark } from 'lucide-react';
import { displayComplianceDate, displayLocalDate } from './compliance-decode';
import type { RecordedParameter, RuleProvenance } from './compliance-decode';
import type { FilingRegisterRecordKind } from './compliance-decode';

/**
 * A page's sticky decision column, from `lg` (the send page, its handoff, the signer page): it sticks
 * clear of the host's sticky app bar, exactly as the workbench's column does
 * (`filing-workbench-pages.tsx`): the bar's height (`--charity-app-bar-h`, the one host contract this
 * package reads; 0px where a host has no bar), then 24px. A bare `top-24` (cut 1003-7's send page,
 * copied by cut 1004-5's handoff and cut 1003-1's signer page) stuck the column 24px from the top,
 * under the console's 56px bar, which hid its heading (cut 1006-4's renders). No max-height and no
 * inner scroll: a column taller than the room below the bar reaches its end at the page's end, where
 * the two-column grid pushes it up.
 */
export const STICKY_COLUMN_CLASSES = 'lg:sticky lg:top-[calc(var(--charity-app-bar-h,0px)+24px)]';

/**
 * The module's not-available state.
 *
 * Reached when the backend's layered gates answer 404 — which is also what a never-deployed route
 * looks like, deliberately. Neutral by design: no error styling, no retry, no toast. The module is
 * simply not part of this environment.
 */
export function ModuleUnavailableNotice({ testId }: { testId: string }) {
  return (
    <EmptyState
      icon={<Icon icon={Landmark} size={20} aria-hidden />}
      title="Not available yet"
      description="Compliance isn’t part of this environment yet."
      testId={testId}
    />
  );
}

/**
 * The record-shaped tone for a register entry.
 *
 * ⚠ TONE IS NOT A VERDICT. `success` marks that a receipt is ON FILE — a document exists — and never
 * that the organization is in good standing anywhere. `warning` marks a window that PASSED with no
 * filing recorded, which is a date arithmetic fact, not an accusation. Nothing here scores or rates
 * anything, and no arrangement of these tones adds up to an assessment.
 */
export function recordTone(kind: FilingRegisterRecordKind): 'neutral' | 'success' | 'warning' {
  switch (kind) {
    case 'registration_filing_recorded':
    case 'exemption_filing_recorded':
    case 'renewal_filing_recorded':
      return 'success';
    case 'refiling_window_passed':
      return 'warning';
    default:
      return 'neutral';
  }
}

/** The record-shaped state chip. */
export function RecordBadge({
  kind,
  label,
  testId,
}: {
  kind: FilingRegisterRecordKind;
  label: string;
  testId?: string;
}) {
  return (
    <Badge tone={recordTone(kind)} testId={testId}>
      {label}
    </Badge>
  );
}

/**
 * The staleness line (set C Q10; Jonathan, 2026-09-29 18:17 PDT, "agree"): kept ONCE, on the rule's
 * own detail view, and never on an intake or calendar source line. It is the module's own honest
 * bound on its corpus and carries no legal conclusion — not a disclaimer or a consult-counsel line,
 * which are registry-served (`compliance-disclosure.tsx`).
 */
export const LAWS_CHANGE_LINE = 'Laws change; re-read the source at filing time.';

/**
 * The rule's own view: its heading, and the words of the door into it where a source line has no
 * citation to carry the door (CCM-BE11-B; cut 1005-2's GO, Q4 — the view's own approved heading in a
 * second place).
 */
export const RULE_VIEW_TITLE = 'About this rule';

/**
 * Which form of the source line a surface shows (set C Q10):
 *   • `intake` — the add sheet, the Calendar and the filing panel (frames 03, 04, 18, 19): the
 *     label, the dated wording and the official link, and NO staleness tail;
 *   • `rule-view` — the rule's own detail view: the same, with {@link LAWS_CHANGE_LINE} once.
 */
export type RuleProvenanceLineForm = 'intake' | 'rule-view';

/**
 * The source line's date, worded by what happened that day (set C Q11, "agree" 18:36 PDT; CCM-BE12):
 * "source checked on «date»" only where the official source was read against its live version that
 * day, and "recorded from Givmo’s research notes on «date»" otherwise.
 *
 * ⚠ The date and its basis are the backend's (`rule_source_date`, `rule_source_date_basis`), which
 * resolves "the governing entry's date, or a later unchanged check of it"; this module never
 * computes which. ⚠ A basis this build cannot read takes the RESEARCH-NOTES wording: a source is
 * never called checked unless the wire says it was read live.
 */
export function sourceDateWording(
  provenance: Pick<RuleProvenance, 'ruleSourceDate' | 'ruleSourceDateBasis'>,
): string | undefined {
  if (provenance.ruleSourceDate.length === 0) return undefined;
  const shown = displayComplianceDate(provenance.ruleSourceDate);
  if (shown === '—') return undefined;
  const date = unbroken(shown);
  return provenance.ruleSourceDateBasis === 'checked_live'
    ? `source checked on ${date}`
    : `recorded from Givmo’s research notes on ${date}`;
}

/**
 * A served date as the source line draws it: its spaces non-breaking, so a narrow line wraps around
 * the date and never inside it ("Jul 3, 2026" stays whole at 390; cut 1005-2's round 1). The words are
 * the same.
 */
function unbroken(date: string): string {
  return date.replace(/ /g, '\u00a0');
}

/**
 * The wording as the line's first words, where no citation or door comes before it (CCM-BE11-B): the
 * same words with a capital, as the reminder emails already write them.
 */
function sentenceCase(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The source line's second sentence while a possible change to a source is under review (CCM-OPS1-WEB;
 * Jonathan's Q3, 2026-09-30, "Approve as written"; the line as frames a1, a2, a4 and a6 show it). It
 * follows the line's own dated sentence and never replaces its date, worded by the same basis:
 * "A later check, on «date», …" only where the line says the source was read live, and the
 * research-notes form otherwise. ⚠ A basis this build cannot read takes the research-notes form, as
 * the date does ({@link sourceDateWording}): it names "one of this rule’s official sources" and never
 * presumes an earlier live read. And "this source" is said only where it is true (CCM-BE11-B's fold):
 * where the backend says a change sits on another of the rule's sources than the one the line names
 * (`ruleSourceChangeOnLineSource` false), the research-notes form, whatever the basis; absent, the
 * basis alone decides, as before.
 */
export function sourceChangeWording(
  provenance: Pick<
    RuleProvenance,
    'ruleSourceChangeFoundOn' | 'ruleSourceDateBasis' | 'ruleSourceChangeOnLineSource'
  >,
): string | undefined {
  const shown = displayComplianceDate(provenance.ruleSourceChangeFoundOn);
  if (shown === '—') return undefined;
  const found = unbroken(shown);
  return provenance.ruleSourceDateBasis === 'checked_live' &&
    provenance.ruleSourceChangeOnLineSource !== false
    ? `A later check, on ${found}, found a possible change to this source; Givmo is reviewing it.`
    : `A check on ${found} found a possible change to one of this rule’s official sources; Givmo is reviewing it.`;
}

/**
 * The same note in the Calendar row's short form (Q3; ruling 6, frame a3): the rows carry no source
 * line, so it follows the row's basis, the rule the date is counted from.
 */
export function sourceChangeShortForm(foundOn: string | undefined): string | undefined {
  const found = displayComplianceDate(foundOn);
  return found === '—' ? undefined : `possible source change found ${found} · under review`;
}

/**
 * The organization's own pending re-check (CCM-OPS1-WEB; Q3; ruling 7, frame a5). The request is an
 * instant, so its day is the viewer's own ({@link displayLocalDate}); `timeZone` names another zone.
 * Only a filing's panel shows it: the rule's own view reuses the same read but carries no line for it.
 */
export function sourceRecheckNote(
  provenance: RuleProvenance,
  timeZone?: string,
): string | undefined {
  const requested = displayLocalDate(provenance.ruleSourceRecheckRequestedAt, timeZone);
  return requested === '—'
    ? undefined
    : `Givmo is re-checking this rule’s official sources (requested ${requested}).`;
}

/**
 * The provenance line: what was cited, when — and on what basis — that source was dated, and,
 * separately, a link to the official source where one exists (web #608's layout, frame 03's words).
 *
 * The citation is the backend's display citation and nothing else (CCM-BE11-B, D-CCM-34): the
 * governing entry's short label, or the label of the rule's freshest live-checked source, which the
 * date beside it then dates. Where the backend serves neither, the line shows NO citation text — the
 * dated wording opens it, as a sentence, and the official link follows. The library's research
 * record (file names, capture notes, superseded figures) never prints: the decode doesn't read it.
 *
 * ⚠ The citation is plain text and the link is its own element, because the two can come from
 * DIFFERENT provenance entries: where the governing entry carries no URL the backend links the rule's
 * freshest primary capture instead (CCM-BE8), which can be another document than the one cited.
 * Wrapping the citation in that URL would present one document as the location of another. No
 * `ruleSourceUrl`, no link.
 *
 * A screen can render several "Official source" links, so each is DESCRIBED by its line's citation
 * (`aria-describedby`), or by the line's dated wording where it has no citation: a screen reader's
 * link list must be able to tell them apart. The `sr-only` new-tab notice follows the house pattern
 * for `target="_blank"` links.
 *
 * On the filing panel the citation is the door to the rule's own view (design go D2): an address
 * inside the module, never a document's, so it presents no document as another's location. Its
 * accessible name says where it goes ("About this rule: «citation»", the visible words included);
 * what a sighted reader sees is the citation alone. Where the line has no citation, the door reads
 * {@link RULE_VIEW_TITLE}, the view's own heading, in the citation's place (cut 1005-2's GO).
 *
 * While a possible change to one of the rule's sources is under review, its sentence
 * ({@link sourceChangeWording}) follows the dated sentence, before the rule view's tail and the
 * official link, everywhere the line shows (CCM-OPS1-WEB).
 */
export function RuleProvenanceLine({
  provenance,
  form = 'intake',
  citationLink,
}: {
  provenance: RuleProvenance;
  /** `intake` by default; only the rule's own detail view passes `rule-view` (Q10). */
  form?: RuleProvenanceLineForm;
  /** The rule's own view, where a surface offers it: its address, and how to go there in place. */
  citationLink?: { href: string; onFollow: () => void };
}) {
  const { ruleSourceUrl } = provenance;
  // CCM-BE11-B (D-CCM-34): the display citation, or no citation text. Never the research record.
  const citation = provenance.ruleDisplayCitation ?? '';
  const wording = sourceDateWording(provenance);
  const change = sourceChangeWording(provenance);
  const citationId = useId();
  const wordingId = useId();
  if (citation.length === 0 && wording === undefined) return null;
  const cited = citation.length > 0;
  // The door: on the citation where there is one, else on the view's own heading in its place.
  const door = citationLink !== undefined;
  const opensLine = cited || door;
  return (
    <p className="text-s12 text-neutral-600" {...testIdProp('compliance-rule-provenance')}>
      {door ? (
        <a
          href={citationLink.href}
          {...(cited ? { 'aria-label': `${RULE_VIEW_TITLE}: ${citation}` } : {})}
          onClick={(event) => {
            // A plain click stays in place; a modified one does what the browser does with a link.
            if (
              event.button !== 0 ||
              event.metaKey ||
              event.ctrlKey ||
              event.shiftKey ||
              event.altKey
            )
              return;
            event.preventDefault();
            citationLink.onFollow();
          }}
          className="underline decoration-border-strong underline-offset-2 hover:text-text-strong"
          {...testIdProp(cited ? 'compliance-rule-citation' : 'compliance-rule-view-door')}
        >
          {/* The official link is described by the citation alone, not by where this one goes. */}
          {cited ? <span id={citationId}>{citation}</span> : RULE_VIEW_TITLE}
        </a>
      ) : (
        cited && (
          <span id={citationId} {...testIdProp('compliance-rule-citation')}>
            {citation}
          </span>
        )
      )}
      {wording !== undefined && (
        <>
          {opensLine ? ' — ' : ''}
          <span id={wordingId} {...testIdProp('compliance-rule-source-date')}>
            {opensLine ? wording : sentenceCase(wording)}
          </span>
          .
          {/* The change adds to the line's date and never stands without it (the backend's own
              field note: "A client keeps the line's date and adds …"). */}
          {change !== undefined && (
            <>
              {' '}
              <span {...testIdProp('compliance-rule-source-change')}>{change}</span>
            </>
          )}
        </>
      )}
      {form === 'rule-view' && (
        <>
          {' '}
          <span {...testIdProp('compliance-rule-laws-change')}>{LAWS_CHANGE_LINE}</span>
        </>
      )}
      {ruleSourceUrl !== undefined && (
        <>
          {' '}
          <a
            href={ruleSourceUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="whitespace-nowrap underline underline-offset-2"
            {...(cited
              ? { 'aria-describedby': citationId }
              : wording !== undefined
                ? { 'aria-describedby': wordingId }
                : {})}
            {...testIdProp('compliance-rule-source-link')}
          >
            Official source <span className="sr-only">(opens in a new tab)</span>
          </a>
        </>
      )}
    </p>
  );
}

/** The organization's own recorded facts, rendered as facts it recorded and nothing more. */
export function RecordedParameterList({
  parameters,
  heading = 'What you recorded',
}: {
  parameters: readonly RecordedParameter[];
  heading?: string;
}) {
  if (parameters.length === 0) return null;
  return (
    <div {...testIdProp('compliance-recorded-parameters')}>
      <p className="text-s12 font-w600 text-neutral-700">{heading}</p>
      <ul className="mt-4 flex flex-col gap-2">
        {parameters.map((parameter) => (
          <li key={parameter.parameterKey} className="text-s12 text-neutral-600">
            <span className="font-w500">{parameter.parameterLabel}:</span> {parameter.recordedValue}
            {parameter.recordedOn.length > 0 && (
              <span className="text-neutral-500"> (recorded {parameter.recordedOn})</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The rule form — the ONE shape this module speaks in.
 *
 * Obligation detail renders it. The retired legacy queue rendered it too, so a rule read the same
 * before and after the organization confirmed it. That symmetry was deliberate: a confirm step
 * whose copy grew more confident afterwards would have been persuading rather than informing.
 */
export function RuleForm({
  jurisdictionName,
  jurisdictionCode,
  ruleSummary,
  provenance,
  recordedParameters,
  children,
  testId,
}: {
  jurisdictionName: string;
  jurisdictionCode: string;
  ruleSummary: string;
  provenance: RuleProvenance;
  recordedParameters: readonly RecordedParameter[];
  /** Trailing content — the record, on detail. */
  children?: ReactNode;
  testId?: string;
}) {
  return (
    <Card {...testIdProp(testId)}>
      <CardBody className="flex flex-col gap-12">
        <div className="flex items-start gap-8">
          <Icon icon={FileText} size={18} className="mt-2 shrink-0 text-neutral-500" />
          <div className="flex flex-col gap-4">
            <p className="text-s14 font-w600 text-neutral-900">
              {jurisdictionName.length > 0 ? jurisdictionName : jurisdictionCode}
            </p>
            {ruleSummary.length > 0 && (
              <p className="text-s13 text-neutral-700" {...testIdProp('compliance-rule-summary')}>
                {ruleSummary}
              </p>
            )}
            {/* The rule's own detail view (Q10): the one place the staleness line stays. */}
            <RuleProvenanceLine provenance={provenance} form="rule-view" />
          </div>
        </div>
        <RecordedParameterList parameters={recordedParameters} />
        {children}
      </CardBody>
    </Card>
  );
}
